import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { normalizeProject } from "../../design/domain";
import { useApp } from "../../design/AppContext";
import { apiDelete, apiGet } from "../../utils/api";
import { apiErrorMessage } from "../../utils/apiError";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Banner, Progress, RowButton, StatusChip } from "../../components/mui";
import { SkeletonRows } from "../../components/loading";
import { MdCheck, MdCircle, MdErrorOutline, MdRemove, MdWarningAmber } from "react-icons/md";

/**
 * 專案列表（設計稿 #screen-projects，el-pm-template_4.html L940-L1016）
 *
 * 這一版拿掉的兩件事，理由都寫在設計稿上：
 *
 * 1. 卡片檢視。設計稿明文「專案列表卡片檢視　與列表檢視重複，只留一種」——
 *    同一份資料畫兩次，使用者得先決定「要用哪一種看」才能開始看。
 *    ViewToggle 元件本身留著（Project/Detail 的工項清單還在用），只是這一頁不再掛它。
 *
 * 2. 拖曳自訂排序。可拖的握把是一個 13px、沒有文字的符號（硬規則 3 不允許），
 *    而且拖曳這件事在觸控與鍵盤上都做不到。
 *    但**排好的順序是存在後端的**（/Project/Order，跟著帳號走），把入口拆掉不代表那份資料消失 ——
 *    所以改成排序下拉裡的一個選項「我自己排的順序」，已經排過的人看到的順序完全不變。
 *
 * 版面規則：外層不再包一層頁面容器，Layout.js 的內容區本身已含內距與寬度上限。
 */

/* ===================== /Project/Order 的後端契約（不要改） =====================
 *
 * GET    /Project/Order            → { project_ids: [12, 7, 3] }  這個人自己排的順序
 * PUT    /Project/Order  { projectIds: [...] }                    覆寫成新的順序
 * DELETE /Project/Order                                           清掉，改回預設（新的在前）
 *
 * 現在只剩 GET（套用順序）與 DELETE（清除）有畫面入口 ——
 * PUT 是拖曳存檔用的，拖曳拿掉之後這一版沒有地方會寫入新順序。
 * 端點與參數名一個字都沒動，之後若要補上「自訂排序」的可觸控版本（上移／下移按鈕），
 * 直接 apiPutJson("/Project/Order", { projectIds: ids }) 就接得回去。
 * ============================================================================ */

/** 只留正整數 id —— 缺 id 的列（後端欄位缺漏）送上去會讓整份順序被後端拒收 */
const toIds = (arr) =>
  (Array.isArray(arr) ? arr : []).map(Number).filter((n) => Number.isInteger(n) && n > 0);

/**
 * 套用個人順序。
 * 不在自訂順序裡的專案（之後才建立、或剛被加進成員）放最上面、新的在前，接著才是自訂順序：
 * 塞到最底下的話，專案一多就等於找不到剛開的案子。
 * 自訂順序裡已經不存在的 id（刪掉、權限變了）不會出現在 projects 裡，自然略過。
 */
function applyOrder(projects, order) {
  const rank = new Map(order.map((id, i) => [id, i]));
  const fresh = projects.filter((p) => !rank.has(Number(p.id))).sort((a, b) => Number(b.id) - Number(a.id));
  const known = projects
    .filter((p) => rank.has(Number(p.id)))
    .sort((a, b) => rank.get(Number(a.id)) - rank.get(Number(b.id)));
  return [...fresh, ...known];
}

/* ===================== 日期 ===================== */

/** 同年只寫 `8/27`，跨年才補年份（與 normalizeProject 的 range 同一套寫法） */
function fmtDate(v) {
  const d = dayjs(v);
  if (!d.isValid()) return "";
  return d.isSame(dayjs(), "year") ? d.format("M/D") : d.format("YYYY/M/D");
}

/** 排序用的到期時刻；沒有日期的一律排到最後，而不是被當成 1970 年排到最前面 */
function dueValue(p) {
  const d = p.endRaw ? dayjs(p.endRaw) : null;
  return d && d.isValid() ? d.valueOf() : Infinity;
}

/**
 * 這個專案的期限狀態。
 * 已完成／已取消的不算 —— 一個去年結案的專案標「已逾期」只是噪音。
 */
function dueState(p, today) {
  if (p.status === "done" || p.status === "cancelled") return "";
  if (!p.endRaw) return "";
  const d = dayjs(p.endRaw);
  if (!d.isValid()) return "";
  const diff = d.startOf("day").diff(today, "day");
  if (diff < 0) return "late";
  if (diff === 0) return "today";
  return "";
}

/* ===================== 一列要講的話 ===================== */

/**
 * 右側徽章。期限一律搶在狀態前面（同「今天」頁的 tagOf）：
 * 一列只能講一件事，「進行中」跟「今天到期」同時出現的話，真正要看的那個會被稀釋掉。
 *
 * 「還沒開始」是設計稿的用字（L1006），不是 PROJ_STATUS_LABEL 的「規劃中」——
 * 這一頁是給不熟系統的人看的，白話優先（README 規則 7）。
 */
function tagOf(p, today) {
  const ds = dueState(p, today);
  if (ds === "today") return { kind: "attention", mark: <MdWarningAmber />, text: "今天到期" };
  if (ds === "late") return { kind: "attention", mark: <MdWarningAmber />, text: "已逾期" };
  switch (p.status) {
    case "done": {
      const on = fmtDate(p.endRaw);
      return { kind: "done", mark: <MdCheck />, text: on ? `${on} 完成` : "已完成" };
    }
    case "progress":
      return { kind: "progress", mark: <MdCircle size="0.6em" />, text: "進行中" };
    case "paused":
      return { kind: "warn", mark: <MdErrorOutline />, text: "暫停" };
    case "cancelled":
      return { kind: "closed", mark: <MdRemove />, text: "已取消" };
    default:
      return { kind: "closed", mark: <MdRemove />, text: "未開始" };
  }
}

/** 進度條的顏色：完成是綠、期限有狀況是紅、其餘是預設的藍 */
function progressTone(p, today) {
  if (p.status === "done" || Number(p.pct) >= 100) return "done";
  return dueState(p, today) ? "attention" : "progress";
}

/* ===================== 排序 ===================== */

const SORT_OPTIONS = [
  { key: "due", label: "依截止日（近到遠）" },
  { key: "attention", label: "依異常狀況優先" },
  { key: "name", label: "依名稱" },
];
/** 只有真的存過順序的人才看得到這個選項 —— 沒排過的人選了會完全沒反應 */
const SORT_MINE = { key: "mine", label: "自訂順序" };

const byDue = (a, b) => {
  const av = dueValue(a);
  const bv = dueValue(b);
  if (av !== bv) return av - bv;
  // 同一天（或都沒有日期）時新的在前，跟預設順序一致
  return Number(b.id) - Number(a.id);
};

function sortProjects(list, key, order, today) {
  const arr = [...list];
  if (key === "mine") return applyOrder(arr, order);
  if (key === "name") return arr.sort((a, b) => String(a.name).localeCompare(String(b.name), "zh-Hant"));
  if (key === "attention") {
    /* 「有問題」＝ 已經來不及的排最前面，接著是今天到期、暫停；結案的沉到最底下 */
    const rank = (p) => {
      const ds = dueState(p, today);
      if (ds === "late") return 0;
      if (ds === "today") return 1;
      if (p.status === "paused") return 2;
      if (p.status === "done" || p.status === "cancelled") return 4;
      return 3;
    };
    return arr.sort((a, b) => rank(a) - rank(b) || byDue(a, b));
  }
  return arr.sort(byDue);
}

/* ===================== 畫面 ===================== */

function App(props) {
  const navigate = useNavigate();
  const { toast, confirmDelete } = useApp();
  // 預設為 true：首次 render 就顯示「載入中…」，避免閃過一次「還沒有任何專案」
  const [loading, setLoading] = useState(true);

  /* 個人順序（存在後端 user_preference，跟著帳號走） */
  const [order, setOrder] = useState([]);
  // 順序到齊前不畫列表，否則會先閃一次預設順序再跳成自訂順序
  const [orderLoaded, setOrderLoaded] = useState(false);
  /*
   * 自訂順序讀取失敗。這時畫面排的是「最近要交的」，不代表後端沒有存著一份 ——
   * 所以「我自己排的順序」這個選項與「清除」這條路這次都不給走：
   * 在不知道後端存著什麼的情況下清除，等於把他排好的那份直接刪掉。
   */
  const [orderError, setOrderError] = useState(false);
  const [clearing, setClearing] = useState(false);

  /** 使用者自己選的排序；空字串＝還沒選過，由有沒有自訂順序決定預設值 */
  const [sortPick, setSortPick] = useState("");

  const { GET_ProjectList, list } = props;

  useEffect(() => {
    // 設計稿的專案列表沒有分頁；後端在 Page/PageCount <= 0 時不套用分頁，會回傳全部
    GET_ProjectList({ Page: 0, PageCount: 0, OrderKey: "id", OrderDirect: "DESC" }, null, setLoading);

    let alive = true;
    apiGet("/Project/Order")
      .then((r) => {
        if (!alive) return;
        // 成功但是空的 = 這個人沒排過，照預設排序
        setOrder(toIds(_.get(r, "project_ids", [])));
      })
      // 取不到自訂順序時列表照樣顯示，但要講出來（下面的警示橫幅）
      .catch(() => {
        if (!alive) return;
        setOrderError(true);
      })
      .finally(() => {
        if (alive) setOrderLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, [GET_ProjectList]);

  /*
   * 今天的日界。用字串當 memo 的依賴而不是 dayjs 物件（同「今天」頁）：
   * dayjs() 每次 render 都是新物件，放進依賴陣列等於每次都重算。
   */
  const todayKey = dayjs().format("YYYY-MM-DD");
  const today = useMemo(() => dayjs(todayKey).startOf("day"), [todayKey]);

  // 後端欄位命名不一（snake_case / PascalCase），一律先過 normalizeProject；
  // /Project/List 回的可能是 { data_list, total_count } 也可能是純陣列，兩種都吃
  const projects = useMemo(() => {
    const raw = Array.isArray(list) ? list : _.get(list, "data_list", []);
    return (Array.isArray(raw) ? raw : []).map(normalizeProject);
  }, [list]);

  const hasCustomOrder = !orderError && order.length > 0;
  /* 沒自己選過排序時：排過順序的人看自己的順序，沒排過的看「最近要交的」 */
  const sortKey = sortPick || (hasCustomOrder ? "mine" : "due");

  const shown = useMemo(
    () => (orderLoaded ? sortProjects(projects, sortKey, order, today) : []),
    [projects, sortKey, order, today, orderLoaded]
  );

  const busy = loading || !orderLoaded;
  const projCount = projects.length;

  /**
   * 標題底下那一句統計（設計稿 L946「5 個專案，其中 1 個今天到期、2 個已完成。」）。
   * 每個數字都從實際資料算出來，沒有一個是寫死的；算出來是 0 的那一段就整段不寫，
   * 免得變成「其中 0 個今天到期」這種讀了也沒用的句子。
   */
  const summary = useMemo(() => {
    if (busy) return "載入中…";
    if (projCount === 0) return "尚無專案。";
    const late = projects.filter((p) => dueState(p, today) === "late").length;
    const dueToday = projects.filter((p) => dueState(p, today) === "today").length;
    const done = projects.filter((p) => p.status === "done").length;
    const parts = [];
    if (dueToday > 0) parts.push(`${dueToday} 個今天到期`);
    if (late > 0) parts.push(`${late} 個已逾期`);
    if (done > 0) parts.push(`${done} 個已完成`);
    return parts.length ? `${projCount} 個專案，其中${parts.join("、")}。` : `${projCount} 個專案。`;
  }, [busy, projCount, projects, today]);

  /**
   * 清除自己排的順序。
   *
   * 這一版沒有地方寫得回去（拖曳已經拿掉），所以清掉之後就真的回不來了 ——
   * 借 confirmDelete 的對話框把這句後果講清楚，不要讓人按一下就沒了。
   */
  const clearOrder = () => {
    if (clearing || orderError) return;
    confirmDelete({
      title: "確定清除自訂順序？",
      subtitle: "僅影響您個人的檢視順序，不會變更任何專案",
      body: "清除後此選項將移除，目前無法重新設定自訂順序。之後清單將依「依截止日（近到遠）」排序顯示。",
      confirmText: "清除",
      onConfirm: () => {
        setClearing(true);
        apiDelete("/Project/Order")
          .then(() => {
            setOrder([]);
            setSortPick("due");
            toast("已清除自訂順序");
          })
          .catch((e) => toast(apiErrorMessage(e, "清除順序失敗"), "error"))
          .finally(() => setClearing(false));
      },
    });
  };

  const options = hasCustomOrder ? [SORT_MINE, ...SORT_OPTIONS] : SORT_OPTIONS;

  return (
    <>
      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
        <Typography variant="h1" component="h1">
          專案
        </Typography>
        <Box sx={{ flex: "1 1 auto" }} />
        {/* 整頁只有這一顆主要按鈕（硬規則 1） */}
        <Button variant="contained" onClick={() => navigate("/Project/Add")}>
          新增專案
        </Button>
      </Stack>

      <Typography sx={{ mt: "var(--space-3)" }}>{summary}</Typography>

      {/* 列表還沒到齊時先畫出幾列輪廓；順序沒到之前不畫真的列表，理由見上面 orderLoaded */}
      {busy && (
        <Card sx={{ mt: "var(--space-4)" }}>
          <SkeletonRows rows={5} label="專案清單載入中" />
        </Card>
      )}

      {/*
        排序讀不到是一件有後果的事：畫面上的順序跟他習慣的不一樣，
        而他自己排的那一份其實還在後端。寫成橫幅而不是灰字小字（硬規則 4）。
      */}
      {orderError && (
        <Banner kind="warn" role="alert" icon={<MdErrorOutline />} sx={{ mt: "var(--space-4)" }}>
          <strong>自訂順序載入失敗　</strong>
          清單暫依下方選擇的排序顯示。您的自訂順序仍保留，請重新整理後再試一次。
        </Banner>
      )}

      {/* 一個專案都沒有的時候不畫工具列（硬規則 2：空的區塊不要畫出來） */}
      {!busy && projCount > 0 && (
        <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", py: "var(--space-4)" }}>
          <Typography variant="body2" color="text.secondary" component="label" htmlFor="psort">
            排序：
          </Typography>
          <TextField
            select
            id="psort"
            value={sortKey}
            onChange={(e) => setSortPick(e.target.value)}
            slotProps={{ select: { native: true } }}
            sx={{ width: "auto" }}
          >
            {options.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </TextField>
          {sortKey === "mine" && (
            <Button variant="text" onClick={clearOrder} disabled={clearing} loading={clearing} loadingPosition="start">
              {clearing ? "清除中…" : "清除自訂順序"}
            </Button>
          )}
        </Stack>
      )}

      {!busy && projCount === 0 && (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h2" component="p">
            尚無專案
          </Typography>
          <Typography>請點選右上角的「新增專案」建立第一個專案，成員與工項皆隸屬於專案之下。</Typography>
        </Box>
      )}

      {!busy && projCount > 0 && (
        <Card>
          {shown.map((p) => {
            const ds = dueState(p, today);
            const tag = tagOf(p, today);
            /* 還沒有工項的專案算不出進度，整條進度條不畫（設計稿最後一列的做法） */
            const showProgress = p.tasks > 0;
            const pct = Math.max(0, Math.min(100, Math.round(Number(p.pct) || 0)));
            const range = p.range && p.range !== "—" ? p.range : "";
            return (
              /*
                專案列沒有型別徽章（工項才有型別），也沒有專案代碼與客戶名稱：
                代碼在專案詳情頁還看得到；客戶名稱這個庫裡幾乎每一筆都一樣（空的顯示「內部」），
                一欄永遠相同的值只是佔位置（設計稿 L956 的註解）。
              */
              <RowButton
                key={p.id ?? p.code}
                tone={ds ? "attention" : ""}
                onClick={() => navigate(`/Project/Detail/${p.id}`)}
                title={p.name || "（未命名）"}
                meta={
                  <>
                    <span>{p.members > 0 ? `${p.members} 個人` : "尚無成員"}</span>
                    <span>{p.tasks > 0 ? `${p.tasks} 件工項` : "尚無工項"}</span>
                    <span>{range || "尚未排定時程"}</span>
                  </>
                }
                side={
                  <>
                    {/* 180px 是設計稿 L969 的進度條寬度；是寬度、不是字級，大字模式不受影響 */}
                    {showProgress && (
                      <Progress value={pct} tone={progressTone(p, today)} ariaLabel={`進度 ${pct}%`} sx={{ width: 180 }} />
                    )}
                    <StatusChip kind={tag.kind} mark={tag.mark}>
                      {tag.text}
                    </StatusChip>
                  </>
                }
              />
            );
          })}
        </Card>
      )}
    </>
  );
}

const mapStateToProps = (state) => ({
  list: _.get(state, "Project.List", {}),
});

const mapDispatchToProps = (dispatch) => ({
  GET_ProjectList: (payload, callback, loading) =>
    dispatch({ type: "GET_ProjectList", payload, callback, loading }),
});

App.propTypes = {
  list: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  GET_ProjectList: PropTypes.func,
};

App.defaultProps = {
  list: {},
  GET_ProjectList: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
